"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { cn } from '../../../lib/utils'
import { Table, ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal } from 'lucide-react'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { ScrollArea } from '../../ui/scroll-area'

interface TableColumn {
  key: string
  label: string
  sortable?: boolean
  align?: 'left' | 'center' | 'right'
  width?: string
  format?: (value: any) => string | React.ReactNode
}

interface TableRow {
  id: string
  [key: string]: any
}

interface TableWidgetProps {
  id: string
  title: string
  description?: string
  columns: TableColumn[]
  rows: TableRow[]
  className?: string
  height?: number
  glassmorphism?: boolean
  compact?: boolean
  interactive?: boolean
  onRowClick?: (row: TableRow) => void
  sortable?: boolean
  showHeader?: boolean
}

export function TableWidget({
  id,
  title,
  description,
  columns,
  rows,
  className,
  height = 300,
  glassmorphism = false,
  compact = false,
  interactive = true,
  onRowClick,
  sortable = true,
  showHeader = true
}: TableWidgetProps) {
  const [sortColumn, setSortColumn] = React.useState<string | null>(null)
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc')
  
  const tableHeight = compact ? 150 : height

  // Sıralama fonksiyonu
  const handleSort = (columnKey: string) => {
    if (!sortable) return
    
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  // Sıralanmış verileri al
  const sortedRows = React.useMemo(() => {
    if (!sortColumn) return rows
    
    return [...rows].sort((a, b) => {
      const aValue = a[sortColumn]
      const bValue = b[sortColumn]
      
      if (aValue === bValue) return 0
      
      const comparison = aValue < bValue ? -1 : 1
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [rows, sortColumn, sortDirection])

  // Sütun genişliklerini hesapla
  const columnWidth = compact ? 'min-w-[60px]' : 'min-w-[100px]'

  return (
    <Card className={cn(
      "relative overflow-hidden",
      glassmorphism && "bg-background/60 backdrop-blur-md border-white/10",
      className
    )}>
      {/* Header */}
      {showHeader && (
        <CardHeader className={cn(
          "flex flex-row items-center justify-between space-y-0",
          compact ? "pb-1 pt-2 px-3" : "pb-3"
        )}>
          <div className="flex items-center gap-2">
            <Table className={cn(
              "text-muted-foreground",
              compact ? "h-3.5 w-3.5" : "h-4 w-4"
            )} />
            <CardTitle className={cn(
              "font-semibold",
              compact ? "text-xs" : "text-base"
            )}>{title}</CardTitle>
          </div>
          
          {description && !compact && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </CardHeader>
      )}

      {/* Table Content */}
      <CardContent className={cn(
        "p-0",
        compact ? "px-1" : "px-2"
      )}>
        <ScrollArea className="w-full" style={{ height: tableHeight }}>
          <div className="w-full">
            <table className="w-full">
              {/* Table Header */}
              <thead className={cn(
                "sticky top-0 z-10 bg-muted/50 backdrop-blur-sm",
                compact && "text-[10px]"
              )}>
                <tr className="border-b">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={cn(
                        "font-medium text-left",
                        compact ? "px-1 py-1" : "px-3 py-2",
                        column.align === 'center' && "text-center",
                        column.align === 'right' && "text-right",
                        columnWidth,
                        column.width
                      )}
                    >
                      {column.sortable !== false && sortable ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "h-auto p-0 font-medium hover:bg-transparent",
                            compact ? "text-[10px]" : "text-xs"
                          )}
                          onClick={() => handleSort(column.key)}
                        >
                          <span>{column.label}</span>
                          {sortColumn === column.key ? (
                            sortDirection === 'asc' ? (
                              <ArrowUp className={cn(
                                "ml-1",
                                compact ? "h-2.5 w-2.5" : "h-3 w-3"
                              )} />
                            ) : (
                              <ArrowDown className={cn(
                                "ml-1",
                                compact ? "h-2.5 w-2.5" : "h-3 w-3"
                              )} />
                            )
                          ) : (
                            <ArrowUpDown className={cn(
                              "ml-1 opacity-50",
                              compact ? "h-2.5 w-2.5" : "h-3 w-3"
                            )} />
                          )}
                        </Button>
                      ) : (
                        <span>{column.label}</span>
                      )}
                    </th>
                  ))}
                  {interactive && !compact && (
                    <th className="w-8"></th>
                  )}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length + (interactive && !compact ? 1 : 0)}
                      className={cn(
                        "text-center text-muted-foreground",
                        compact ? "py-4 text-xs" : "py-8"
                      )}
                    >
                      No data available
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row, rowIndex) => (
                    <motion.tr
                      key={row.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: rowIndex * 0.02 }}
                      className={cn(
                        "border-b transition-colors",
                        interactive && "hover:bg-muted/50 cursor-pointer"
                      )}
                      onClick={() => interactive && onRowClick?.(row)}
                    >
                      {columns.map((column) => {
                        const value = row[column.key]
                        const formattedValue = column.format ? column.format(value) : value
                        
                        return (
                          <td
                            key={column.key}
                            className={cn(
                              compact ? "px-1 py-1 text-[10px]" : "px-3 py-2 text-sm",
                              column.align === 'center' && "text-center",
                              column.align === 'right' && "text-right"
                            )}
                          >
                            {React.isValidElement(formattedValue) ? (
                              formattedValue
                            ) : (
                              <span className={cn(
                                compact && "line-clamp-1"
                              )}>
                                {formattedValue}
                              </span>
                            )}
                          </td>
                        )
                      })}
                      {interactive && !compact && (
                        <td className="px-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation()
                              // Action menu logic here
                            }}
                          >
                            <MoreHorizontal className="h-3 w-3" />
                          </Button>
                        </td>
                      )}
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </ScrollArea>

        {/* Footer info */}
        {!compact && rows.length > 0 && (
          <div className="px-3 py-2 border-t text-xs text-muted-foreground">
            Showing {sortedRows.length} of {rows.length} rows
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// Helper function for common formatters
export const tableFormatters = {
  currency: (value: number) => `$${value.toLocaleString()}`,
  percentage: (value: number) => `${value}%`,
  date: (value: string | Date) => new Date(value).toLocaleDateString(),
  badge: (value: string, variant?: any) => (
    <Badge variant={variant}>{value}</Badge>
  ),
  status: (value: string) => {
    const statusColors: Record<string, string> = {
      active: 'bg-green-500',
      inactive: 'bg-gray-500',
      pending: 'bg-yellow-500',
      error: 'bg-red-500'
    }
    return (
      <span className="flex items-center gap-1">
        <span className={cn(
          "h-2 w-2 rounded-full",
          statusColors[value.toLowerCase()] || 'bg-gray-500'
        )} />
        {value}
      </span>
    )
  }
}

export default TableWidget